import { OpalApiResponseDocument } from '@opallabs/api'
import { readPlan } from '@opallabs/api/BFF'
import { type CascadingMenuOption } from '@opallabs/concepts'

type ReadPlanResponse = OpalApiResponseDocument<
  'ExperimentalReadPlanBFFV1',
  200
>
type AllCategories = ReadPlanResponse['data']['categories']['by_id']
type Category = AllCategories[string]
type HierachyNode = ReadPlanResponse['data']['categories']['hierarchy'][number]

const getCategoryTitle = (title: string | null, kind: Category['kind']) => {
  if (title) return title
  return kind === 'group' ? 'Untitled Group' : 'Untitled Swimlane'
}

const mapItem = (category: Category): CascadingMenuOption => ({
  value: category.id,
  label: getCategoryTitle(category.title, category.kind),
  disableSelection: category.kind !== 'swimlane',
})
const reduceHierachy = (
  accum: CascadingMenuOption[],
  node: HierachyNode,
  allCategories: AllCategories
) => {
  const parentCategory = allCategories[node.id]
  if (!parentCategory) return accum

  if (parentCategory.kind === 'group') {
    const parentItem: CascadingMenuOption = {
      ...mapItem(parentCategory),
      childOptions: node.children.reduce<CascadingMenuOption[]>(
        (accum, node) =>
          reduceHierachy(accum, node as HierachyNode, allCategories),
        []
      ),
    }

    return [...accum, parentItem]
  }

  if (parentCategory.kind === 'swimlane') {
    return [...accum, mapItem(parentCategory)]
  }

  return accum
}

/**
 * Hook to get swimlane options for a specific plan (for use outside plan context).
 * @note The query will be disabled if the planId is not provided.
 */
export const useSwimlaneOptionsForPlan = (
  {
    planId,
    workspaceUuid,
  }: {
    planId: string | undefined
    workspaceUuid: string
  },
  { enabled }: { enabled?: boolean } = {}
) => {
  return readPlan.useQuery({
    planId: planId!,
    workspaceUuid,
    select: (response) => {
      if (!response) return []

      const allCategories = response.data.categories.by_id

      return response.data.categories.hierarchy.reduce<CascadingMenuOption[]>(
        (accum, node) => reduceHierachy(accum, node, allCategories),
        []
      )
    },
    enabled: planId === undefined ? false : enabled,
  })
}
